import React from 'react';
import { X } from 'lucide-react';
import { OnboardingInPossession } from '../../../services/aiIntelligenceService';

const PHILOSOPHIES = [
  { value: 'build-from-back', label: 'Build from the Back', description: 'Patient build-up through the thirds' },
  { value: 'direct-play', label: 'Direct Play', description: 'Quick forward passes to exploit space' },
  { value: 'wing-play', label: 'Wing Play', description: 'Use width to stretch the opposition' },
  { value: 'central-overloads', label: 'Central Overloads', description: 'Dominate the middle of the pitch' },
  { value: 'positional-play', label: 'Positional Play', description: 'Structured positioning to create superiority' },
];

const BUILD_UP_STYLES = [
  { value: 'short-passing', label: 'Short Passing', description: 'Keep the ball on the ground with quick combinations' },
  { value: 'long-ball', label: 'Long Ball', description: 'Bypass midfield with direct passes' },
  { value: 'mixed', label: 'Mixed', description: 'Vary between short and long depending on pressure' },
  { value: 'goalkeeper-involved', label: 'GK Involved', description: 'Goalkeeper as an outfield player in build-up' },
];

const PRESSING_TRIGGERS = [
  'Poor first touch',
  'Backwards pass',
  'Ball to fullback',
  'Ball to goalkeeper',
  'Sideways pass',
  'Player isolated',
  'Slow tempo',
  'High defensive line',
];

interface InPossessionStepProps {
  data: OnboardingInPossession;
  onChange: (data: OnboardingInPossession) => void;
}

export function InPossessionStep({ data, onChange }: InPossessionStepProps) {
  const toggleTrigger = (trigger: string) => {
    const current = data.pressingTriggers || [];
    if (current.includes(trigger)) {
      onChange({ ...data, pressingTriggers: current.filter((t) => t !== trigger) });
    } else {
      onChange({ ...data, pressingTriggers: [...current, trigger] });
    }
  };

  return (
    <div>
      <h2 className="text-xl font-bold text-gray-900 mb-2">In Possession</h2>
      <p className="text-sm text-gray-600 mb-6">
        How does your team play when you have the ball?
      </p>

      {/* Philosophy */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          In-Possession Philosophy <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {PHILOSOPHIES.map((item) => (
            <button
              key={item.value}
              type="button"
              onClick={() => onChange({ ...data, philosophy: item.value })}
              className={`text-left p-3 rounded-lg border-2 transition-all min-h-[44px] ${
                data.philosophy === item.value
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <span className="text-sm font-medium text-gray-900">{item.label}</span>
              <span className="block text-xs text-gray-500 mt-0.5">{item.description}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Build-Up Style */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Build-Up Style <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {BUILD_UP_STYLES.map((item) => (
            <button
              key={item.value}
              type="button"
              onClick={() => onChange({ ...data, buildUpStyle: item.value })}
              className={`text-left p-3 rounded-lg border-2 transition-all min-h-[44px] ${
                data.buildUpStyle === item.value
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <span className="text-sm font-medium text-gray-900">{item.label}</span>
              <span className="block text-xs text-gray-500 mt-0.5">{item.description}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Pressing Triggers */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Pressing Triggers <span className="text-gray-400">(select all that apply)</span>
        </label>
        <div className="flex flex-wrap gap-2">
          {PRESSING_TRIGGERS.map((trigger) => (
            <button
              key={trigger}
              type="button"
              onClick={() => toggleTrigger(trigger)}
              className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-all min-h-[32px] ${
                data.pressingTriggers?.includes(trigger)
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 text-gray-600 hover:border-gray-300'
              }`}
            >
              {trigger}
              {data.pressingTriggers?.includes(trigger) && (
                <X className="w-3 h-3 inline ml-1" />
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
